import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '../lib/cn.js';

export function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn(
        'flex gap-4',
        // Radix stamps the orientation on the root; vertical puts the selector
        // beside the panel instead of above it, which is what a settings screen
        // with more than a couple of sections needs.
        'data-[orientation=horizontal]:flex-col data-[orientation=vertical]:flex-row',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Underline tabs, not pills: on a detail screen the tab strip sits directly on
 * the page and a filled pill row would read as a second toolbar.
 */
export function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        'inline-flex items-center gap-4 border-border',
        'data-[orientation=horizontal]:w-full data-[orientation=horizontal]:border-b',
        // A column of its own, wide enough to read and narrow enough to leave
        // the panel the room it needs.
        'data-[orientation=vertical]:w-48 data-[orientation=vertical]:shrink-0',
        'data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch',
        'data-[orientation=vertical]:gap-1 data-[orientation=vertical]:border-r',
        className,
      )}
      {...props}
    />
  );
}

export function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        'relative inline-flex items-center gap-1.5 border-transparent',
        'text-body font-medium text-muted whitespace-nowrap transition-colors',
        'hover:text-ink-2 disabled:pointer-events-none disabled:opacity-50',
        'data-[orientation=horizontal]:-mb-px data-[orientation=horizontal]:justify-center',
        'data-[orientation=horizontal]:border-b-2 data-[orientation=horizontal]:px-0.5',
        'data-[orientation=horizontal]:pb-2',
        // Vertical: the marker is the edge it sits against, so it moves to the
        // right border and the label starts at the left like a list.
        'data-[orientation=vertical]:-mr-px data-[orientation=vertical]:justify-start',
        'data-[orientation=vertical]:border-r-2 data-[orientation=vertical]:px-3',
        'data-[orientation=vertical]:py-2',
        'data-[state=active]:border-accent data-[state=active]:text-ink',
        "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

export function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-hidden', className)}
      {...props}
    />
  );
}
